import { createClient } from "@supabase/supabase-js";
import { clearAppStateForLocalStorage } from "@excalidraw/excalidraw/appState";

export interface ProjectMetadata {
  id: string;
  name: string;
  updatedAt: number;
  sort_order?: number;
}

export interface LocalProject extends ProjectMetadata {
  elements: readonly any[];
  appState: any;
}

const supabaseUrl = import.meta.env.VITE_APP_SUPABASE_URL || "";
const supabaseAnonKey = import.meta.env.VITE_APP_SUPABASE_ANON_KEY || "";

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

const ACTIVE_PROJECT_KEY = "excalidraw-active-project-id";

export const getProjectsList = async (): Promise<ProjectMetadata[]> => {
  try {
    // Ordena pela ordem definida pelo usuário (sort_order)
    const { data, error } = await supabase
      .from("projects")
      .select("id, name, updated_at, sort_order")
      .order("sort_order", { ascending: true });

    if (error) {
      throw error;
    }
    return (data || []).map((p) => ({
      id: p.id,
      name: p.name,
      updatedAt: new Date(p.updated_at).getTime(),
      sort_order: p.sort_order,
    }));
  } catch (error) {
    console.error("Erro ao carregar lista de projetos do Supabase:", error);
    return [];
  }
};

export const saveProjectsList = async (
  list: ProjectMetadata[],
): Promise<void> => {
  try {
    const promises = list.map((item, index) =>
      supabase.from("projects").update({ sort_order: index }).eq("id", item.id),
    );
    await Promise.all(promises);
  } catch (error) {
    console.error("Erro ao salvar ordem dos projetos no Supabase:", error);
  }
};

export const getProject = async (id: string): Promise<LocalProject | null> => {
  try {
    const { data, error } = await supabase
      .from("projects")
      .select("id, name, elements, app_state, updated_at, sort_order")
      .eq("id", id)
      .single();

    if (error) {
      throw error;
    }
    if (!data) {
      return null;
    }

    return {
      id: data.id,
      name: data.name,
      updatedAt: new Date(data.updated_at).getTime(),
      sort_order: data.sort_order,
      elements:
        typeof data.elements === "string"
          ? JSON.parse(data.elements)
          : data.elements,
      appState:
        typeof data.app_state === "string"
          ? JSON.parse(data.app_state)
          : data.app_state,
    };
  } catch (error) {
    console.error(`Erro ao carregar projeto ${id} do Supabase:`, error);
    return null;
  }
};

export const createProject = async (
  name: string,
  elements: readonly any[],
  appState: any,
): Promise<string> => {
  try {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error("Usuário não autenticado");
    }

    const list = await getProjectsList();
    const cleanState = appState ? clearAppStateForLocalStorage(appState) : {};

    const { data, error } = await supabase
      .from("projects")
      .insert({
        name,
        elements,
        app_state: cleanState,
        sort_order: list.length,
        user_id: user.id,
      })
      .select("id")
      .single();

    if (error) {
      throw error;
    }
    return data.id;
  } catch (error) {
    console.error("Erro ao criar projeto no Supabase:", error);
    throw error;
  }
};

export const saveProjectData = async (
  id: string,
  elements: readonly any[],
  appState: any,
): Promise<void> => {
  try {
    const cleanState = appState ? clearAppStateForLocalStorage(appState) : {};
    const { error } = await supabase
      .from("projects")
      .update({
        elements,
        app_state: cleanState,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id);

    if (error) {
      throw error;
    }
  } catch (error) {
    console.error(`Erro ao salvar dados do projeto ${id} no Supabase:`, error);
  }
};

export const renameProject = async (
  id: string,
  newName: string,
): Promise<void> => {
  try {
    const { error } = await supabase
      .from("projects")
      .update({
        name: newName,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id);

    if (error) {
      throw error;
    }
  } catch (error) {
    console.error(`Erro ao renomear projeto ${id} no Supabase:`, error);
  }
};

export const deleteProject = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase.from("projects").delete().eq("id", id);

    if (error) {
      throw error;
    }
  } catch (error) {
    console.error(`Erro ao deletar projeto ${id} no Supabase:`, error);
  }
};

export const getActiveProjectId = (): string | null => {
  try {
    return localStorage.getItem(ACTIVE_PROJECT_KEY);
  } catch (error) {
    console.error("Erro ao obter ID do projeto ativo:", error);
    return null;
  }
};

export const setActiveProjectId = (id: string | null): void => {
  try {
    if (id) {
      localStorage.setItem(ACTIVE_PROJECT_KEY, id);
    } else {
      localStorage.removeItem(ACTIVE_PROJECT_KEY);
    }
  } catch (error) {
    console.error("Erro ao definir ID do projeto ativo:", error);
  }
};
